'use client'

import React, { useEffect, useState } from 'react'
import { useLocale, useTranslations } from 'next-intl'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { getBrandName, VARIANT } from '@/src/utils/buildUtils'
import { featureConfig } from '@/src/utils/featureConfig'
import { getSupportContacts } from '@/src/utils/airspaceAlert/supportContacts'
import { getSupportHref } from '@/src/utils/header/headerUtils'

import { AirspaceAlert } from './airspaceAlert'

interface AlertData {
    alertLabel: string
    messages: string[]
    knowMore: string
    knowMoreLink?: string
    modalTitle: string
    okayText: string
    latestUpdate?: string
    latestUpdatesText?: string
    contactSupportText?: string
    needHelpText?: string
    supportSubtitle?: string
    supportHours?: string
    lastUpdatedLabel?: string
}

const AirspaceAlertWrapper: React.FC = () => {
    const locale = useLocale()
    const t = useTranslations('airspaceAlert')
    const [alertData, setAlertData] = useState<AlertData | null>(null)
    const [modalOpen, setModalOpen] = useState(false)
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768
    const showLatestUpdatesButton = !!featureConfig[VARIANT]?.AIRSPACE_LATEST_UPDATES_BUTTON

    useEffect(() => {
        fetch('https://ui.cltp.co/json/airspace-alert.json')
            .then((res) => res.json())
            .then((data) => {
                if (!data.enable) return null
                const localeData = data[locale] || data['en']
                setAlertData({
                    ...localeData,
                    alertLabel: localeData?.alertLabel || t('alertLabel'),
                    latestUpdate: data.latestUpdate
                })
            })
            .catch(() => {})
    }, [locale, t])

    if (!alertData) return null

    const latestUpdatesUrl = alertData.knowMoreLink || ''
    const brandName = getBrandName() === 'cleartrip' ? 'Cleartrip' : 'Flyin'
    const currentOrigin = typeof window !== 'undefined' ? window.location.origin : ''
    const supportContacts = getSupportContacts(brandName, currentOrigin)

    const openLatestUpdates = () => {
        if (!latestUpdatesUrl || typeof window === 'undefined') return
        window.location.href = latestUpdatesUrl
    }
    const openContactSupport = () => {
        if (typeof window === 'undefined') return
        const supportUrl = brandName === 'Cleartrip' ? '/support' : getSupportHref(locale, true)
        window.location.href = supportUrl
    }

    const formatLatestUpdateDate = (rawDate?: string): string => {
        if (!rawDate) return ''
        const match = rawDate.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
        if (!match) return rawDate

        const [, day, month, year] = match
        const parsedDate = new Date(Number(year), Number(month) - 1, Number(day))
        if (Number.isNaN(parsedDate.getTime())) return rawDate

        const formatter = new Intl.DateTimeFormat(locale, {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        })

        const parts = formatter.formatToParts(parsedDate)
        const dayPart = parts.find(part => part.type === 'day')?.value || day
        const monthPart = parts.find(part => part.type === 'month')?.value || month
        const yearPart = parts.find(part => part.type === 'year')?.value || year

        return `${dayPart} ${monthPart} ${yearPart}`
    }

    const latestUpdatesText = alertData.latestUpdatesText || t('latestUpdates')
    const contactSupportText = alertData.contactSupportText || t('contactSupport')
    const needHelpText = alertData.needHelpText || t('needHelp')
    const supportHours = alertData.supportHours || t('supportHours')
    const supportSubtitle = alertData.supportSubtitle || t('supportSubtitle', { hours: supportHours })
    const lastUpdatedLabel = alertData.lastUpdatedLabel || t('lastUpdated')
    const whatsappLabel = t('whatsappLabel')
    const formattedLatestUpdate = formatLatestUpdateDate(alertData.latestUpdate)

    return (
        <>
            <AirspaceAlert
                alertLabel={alertData.alertLabel}
                message={alertData.modalTitle}
                knowMoreText={alertData.knowMore}
                onKnowMore={() => setModalOpen(true)}
                isMobile={isMobile}
                latestUpdate={formattedLatestUpdate}
                lastUpdatedLabel={lastUpdatedLabel}
            />
            <Dialog
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                maxWidth="sm"
                fullWidth
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: 3,
                            mx: { xs: 2, sm: 2 },
                            my: { xs: 2, sm: 2 },
                            width: { xs: 'calc(100vw - 32px)', sm: 'auto' },
                            maxWidth: '460px',
                        }
                    }
                }}
            >
                <DialogTitle
                    sx={{ pb: 1, pt: 2.5, px: 2.5, display: 'flex', alignItems: 'flex-start', gap: 1 }}
                >
                    <Typography
                        sx={{
                            fontWeight: 700,
                            color: 'textColors.primary',
                            textAlign: 'left',
                            fontSize: '16px',
                            lineHeight: 1.15,
                            flex: 1
                        }}
                    >
                        {alertData.modalTitle}
                    </Typography>
                    <IconButton
                        onClick={() => setModalOpen(false)}
                        sx={{
                            color: '#1A1A1A',
                            p: 0.5,
                            mt: -0.25
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: 2, pb: 0, px: 2.5 }}>
                    {!!formattedLatestUpdate && (
                        <Typography
                            sx={{
                                color: '#5F6368',
                                fontSize: '10px',
                                fontWeight: 400,
                                mb: 1.5
                            }}
                        >
                            {lastUpdatedLabel}: {formattedLatestUpdate}
                        </Typography>
                    )}
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'textColors.secondary',
                            fontSize: '12px',
                            fontWeight: 400,
                            lineHeight: 1.6,
                            textAlign: 'left',
                            whiteSpace: 'pre-line',
                            mb: 2
                        }}
                    >
                        {alertData.messages[0] || ''}
                    </Typography>
                    {!!supportContacts && (
                        <>
                            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1A1A1A', mb: 0.5 }}>
                                {needHelpText}
                            </Typography>
                            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#5F6368', mb: 2 }}>
                                {supportSubtitle}
                            </Typography>
                            <Typography
                                component="a"
                                href={`tel:${supportContacts.international_calls}`}
                                sx={{
                                    display: 'block',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    color: '#1A1A1A',
                                    textDecoration: 'underline',
                                    mb: 2
                                }}
                            >
                                {supportContacts.international_calls}
                            </Typography>
                            <Typography
                                component="a"
                                href={`https://wa.me/${supportContacts.whatsapp.replace(/[^0-9]/g, '')}`}
                                target="_blank"
                                rel="noreferrer"
                                sx={{
                                    display: 'block',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    color: '#1A1A1A',
                                    textDecoration: 'underline',
                                    mb: 2
                                }}
                            >
                                {supportContacts.whatsapp} ({whatsappLabel})
                            </Typography>
                            <Typography
                                component="a"
                                href={`mailto:${supportContacts.email}`}
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    color: '#1A1A1A',
                                    textDecoration: 'underline',
                                    mb: 0
                                }}
                            >
                                {supportContacts.email}
                                <OpenInNewIcon sx={{ fontSize: 16 }} />
                            </Typography>
                        </>
                    )}
                </DialogContent>
                <DialogActions
                    sx={{ px: 2.5, pb: 2.5, justifyContent: 'center', flexDirection: 'column', gap: 1.5 }}
                >
                    {showLatestUpdatesButton && !!latestUpdatesUrl && (
                        <Button
                            variant="customVariantPrimary"
                            onClick={openLatestUpdates}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 600,
                                bgcolor: 'brand.primary',
                                color: 'brand.primaryForeground',
                                py: 1.25,
                                fontSize: '14px',
                                width: '100%',
                                borderRadius: '12px',
                                '&:hover': {
                                    bgcolor: 'brand.primaryHover',
                                }
                            }}
                        >
                            {latestUpdatesText}
                        </Button>
                    )}
                    <Button
                        variant="outlined"
                        onClick={openContactSupport}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            color: 'brand.primary',
                            borderColor: 'brand.primary',
                            py: 1.25,
                            fontSize: '14px',
                            width: '100%',
                            borderRadius: '12px',
                            '&:hover': {
                                borderColor: 'brand.primary',
                                backgroundColor: 'transparent'
                            }
                        }}
                    >
                        {contactSupportText}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export { AirspaceAlertWrapper }
export default AirspaceAlertWrapper
